/**
 * Audio Upload Hook
 *
 * Handles audio recording completion and upload to Firebase Storage.
 * Manages upload progress, storage block creation, and capture mode message creation.
 *
 * @module features/chat/hooks/chat-input/useAudioUpload
 */

import { useState, useCallback, useEffect } from "react";
import { toast } from "sonner";
import { getRecordingError, RecordingError } from "@/constants/toast-messages";
import { useAudioRecording } from "@/hooks/use-audio-recording";
import { uploadAudioToStorage } from "../../services/audio-upload-service";
import { createCaptureContentMessage } from "../../services/capture-service";
import { FEATURES } from "@/config";
import type { StorageAudioBlock, ContentBlock } from "../../types/messages";

// =============================================================================
// TYPES
// =============================================================================

export interface UseAudioUploadOptions {
  userId: string | undefined;
  conversationId: string | undefined;
  contentBlocks: ContentBlock[];
  setContentBlocks: (blocks: ContentBlock[] | ((prev: ContentBlock[]) => ContentBlock[])) => void;
  captureMode: boolean;
  savedBlockIdsRef: React.MutableRefObject<Set<string>>;
}

export interface UseAudioUploadReturn {
  isRecording: boolean;
  recordingDuration: number;
  analyserNode: AnalyserNode | null;
  onStartRecording: () => void;
  onStopRecording: () => void;
  onCancelRecording: () => void;
  isUploadingAudio: boolean;
}

// =============================================================================
// HOOK
// =============================================================================

/**
 * Hook for audio recording and upload functionality.
 *
 * Features:
 * - Wraps useAudioRecording hook
 * - Handles upload to Firebase Storage with progress
 * - Creates storage audio blocks
 * - In capture mode: creates Firestore messages
 * - Shows error toasts on failure
 */
export function useAudioUpload({
  userId,
  conversationId,
  contentBlocks,
  setContentBlocks,
  captureMode,
  savedBlockIdsRef,
}: UseAudioUploadOptions): UseAudioUploadReturn {
  const [isUploading, setIsUploading] = useState(false);

  const {
    state: recordingState,
    duration: recordingDuration,
    startRecording,
    stopRecording,
    cancelRecording,
    analyserNode,
    error: recordingError,
  } = useAudioRecording({ maxDuration: 900 }); // 15 minutes

  const isRecording = recordingState === "recording";

  // Show error toast when recording fails
  useEffect(() => {
    if (recordingError) {
      toast.error(getRecordingError(RecordingError.FAILED), {
        description: recordingError,
      });
    }
  }, [recordingError]);

  const onStartRecording = useCallback(async () => {
    if (FEATURES.audioRecordingEnabled) {
      try {
        await startRecording();
      } catch (error) {
        console.error("Failed to start recording:", error);
      }
    }
  }, [startRecording]);

  const onStopRecording = useCallback(async () => {
    console.log("📼 onStopRecording called");
    const result = await stopRecording();

    if (!result) {
      console.log("❌ No recording result");
      return;
    }

    console.log("✅ Recording result received:", {
      blobSize: result.blob.size,
      mimeType: result.mimeType,
      duration: result.duration,
    });

    if (!userId || !conversationId) {
      console.error("❌ Cannot upload audio: missing userId or conversationId");
      toast.error(getRecordingError(RecordingError.UPLOAD_FAILED), {
        description: "Missing user or conversation context",
      });
      return;
    }

    const blobUrl = URL.createObjectURL(result.blob);
    const tempGcsPath = `audio/temp-${Date.now()}.webm`;

    // Add audio block immediately with uploading state
    const pendingAudioBlock: StorageAudioBlock = {
      type: "audio",
      source_type: "storage",
      mime_type: result.mimeType,
      relative_gcs_path: tempGcsPath,
      metadata: {
        duration: result.duration,
      },
      uploading: true,
      uploadProgress: 0,
    };

    console.log("✅ Adding pending audio block with blob URL");
    setContentBlocks((prev: ContentBlock[]) => [...prev, pendingAudioBlock]);

    console.log("📤 Uploading to Firebase Storage...", { userId, conversationId });
    setIsUploading(true);

    try {
      const uploadResult = await uploadAudioToStorage(
        result.blob,
        userId,
        conversationId,
        {
          duration: result.duration,
          onProgress: (progress) => {
            setContentBlocks((prev: ContentBlock[]) =>
              prev.map((block) =>
                block.type === "audio" &&
                block.source_type === "storage" &&
                (block as StorageAudioBlock).relative_gcs_path === tempGcsPath
                  ? { ...block, uploadProgress: progress }
                  : block
              )
            );
          },
        }
      );

      console.log("✅ Upload successful:", uploadResult);

      // Update the block with the actual storage path
      // Mark as creating Firestore message if in capture mode
      setContentBlocks((prev: ContentBlock[]) =>
        prev.map((block) =>
          block.type === "audio" &&
          block.source_type === "storage" &&
          (block as StorageAudioBlock).relative_gcs_path === tempGcsPath
            ? {
                ...block,
                relative_gcs_path: uploadResult.storagePath,
                uploading: false,
                uploadProgress: undefined,
                creatingMessage: captureMode,
              }
            : block
        )
      );

      console.log("✅ Audio auto-uploaded to storage:", uploadResult.storagePath);

      // In capture mode: create message in backend
      if (captureMode) {
        try {
          const audioBlock = contentBlocks.find(
            (b) =>
              b.type === "audio" &&
              b.source_type === "storage" &&
              (b as StorageAudioBlock).relative_gcs_path === uploadResult.storagePath
          ) as StorageAudioBlock | undefined;

          const response = await createCaptureContentMessage(userId, conversationId, {
            type: "audio",
            file_name: uploadResult.fileName,
            relative_gcs_path: uploadResult.storagePath,
            mime_type: audioBlock?.mime_type || "audio/webm",
            metadata: {
              duration: audioBlock?.metadata?.duration,
            },
          });

          setContentBlocks((prev: ContentBlock[]) =>
            prev.map((b) => {
              if (
                b.type === "audio" &&
                b.source_type === "storage" &&
                (b as StorageAudioBlock).relative_gcs_path === uploadResult.storagePath
              ) {
                return { ...b, messageId: response.message_id, creatingMessage: undefined };
              }
              return b;
            })
          );

          savedBlockIdsRef.current.add(`${uploadResult.storagePath}-audio`);
          console.log(`[CaptureMode] ✅ Audio message created: ${response.message_id}`);
        } catch (error) {
          console.error("[CaptureMode] Failed to create audio message:", error);
          // Clear creatingMessage flag on error so user can see it failed
          setContentBlocks((prev: ContentBlock[]) =>
            prev.map((b) => {
              if (
                b.type === "audio" &&
                b.source_type === "storage" &&
                (b as StorageAudioBlock).relative_gcs_path === uploadResult.storagePath
              ) {
                return { ...b, creatingMessage: undefined };
              }
              return b;
            })
          );
          toast.error("Fout bij opslaan", {
            description: "De audio opname kon niet worden opgeslagen. Probeer het opnieuw.",
          });
        }
      }

      URL.revokeObjectURL(blobUrl);
    } catch (error) {
      console.error("❌ Failed to upload audio:", error);
      setContentBlocks((prev: ContentBlock[]) =>
        prev.filter(
          (block) =>
            !(
              block.type === "audio" &&
              block.source_type === "storage" &&
              (block as StorageAudioBlock).relative_gcs_path === tempGcsPath
            )
        )
      );
      URL.revokeObjectURL(blobUrl);
      toast.error(getRecordingError(RecordingError.UPLOAD_FAILED), {
        description: error instanceof Error ? error.message : undefined,
      });
    } finally {
      setIsUploading(false);
    }
  }, [stopRecording, userId, conversationId, setContentBlocks, contentBlocks, captureMode, savedBlockIdsRef]);

  const onCancelRecording = useCallback(() => {
    cancelRecording();
  }, [cancelRecording]);

  return {
    isRecording,
    recordingDuration,
    analyserNode,
    onStartRecording,
    onStopRecording,
    onCancelRecording,
    isUploadingAudio: isUploading,
  };
}
